import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";
import { Tab } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";

// Document-style markdown renderers — classic, book-like typography (serif body,
// justified paragraphs, traditional list markers) for the printable summary page.
const documentMarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="my-4 leading-[1.85] text-slate-700 text-justify">{children}</p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="my-4 space-y-2 list-disc pl-6 marker:text-[#47b3fe]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 space-y-2 list-decimal pl-6 marker:text-[#47b3fe] marker:font-semibold">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="pl-1.5 leading-[1.85] text-slate-700">{children}</li>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#47b3fe] font-medium underline decoration-blue-200 underline-offset-2"
    >
      {children}
    </a>
  ),
};

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

interface Flashcard {
  front: string;
  back: string;
}

interface SummarySection {
  title: string;
  content: string;
}

interface SummaryContent {
  emoji: string;
  title: string;
  summary: string;
  sections?: SummarySection[];
}

interface QuizResponse {
  completion: {
    choices: Array<{
      message: {
        content: string;
      };
    }>;
  };
}

type TabType = "summary" | "quiz" | "flashcards";
type QuizDifficulty = "easy" | "medium" | "hard";

const LearningDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const TABS: TabType[] = ["summary", "quiz", "flashcards"];

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("summary");

  const [toast, setToast] = useState<{
    message: string;
    type: "error" | "success";
  } | null>(null);

  const [learningData, setLearningData] = useState<any>(null);
  const [summaryData, setSummaryData] = useState<SummaryContent | null>(null);
  const [quizData, setQuizData] = useState<{
    questions: QuizQuestion[];
  } | null>(null);

  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({});
  const [showResults, setShowResults] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [quizDifficulty, setQuizDifficulty] =
    useState<QuizDifficulty>("medium");
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const [flashcardsData, setFlashcardsData] = useState<{
    cards: Flashcard[];
  } | null>(null);
  const [isGeneratingFlashcards, setIsGeneratingFlashcards] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isCardFlipped, setIsCardFlipped] = useState(false);

  useEffect(() => {
    if (user && id) {
      fetchLearningData();
    }
  }, [user, id]);

  const fetchLearningData = async () => {
    if (!user || !id) return;

    setIsLoading(true);
    setError(null);

    try {
      const learningRef = doc(db, "users", user.uid, "learningHistory", id);
      const learningSnap = await getDoc(learningRef);

      if (!learningSnap.exists()) throw new Error("Learning not found");

      const data = learningSnap.data();
      setLearningData(data);

      if (data.hasContent.summary) {
        if (activeTab !== "quiz" || !data.hasContent.quiz) {
          setActiveTab("summary");
        }

        const summaryRef = doc(
          db,
          "users",
          user.uid,
          "learningHistory",
          id,
          "summary",
          "content"
        );
        const summarySnap = await getDoc(summaryRef);

        if (summarySnap.exists()) {
          const summaryContent = summarySnap.data();
          setSummaryData({
            emoji: summaryContent.emoji,
            title: summaryContent.title,
            summary: summaryContent.summary || "",
            sections: summaryContent.sections || [],
          });
        }
      } else if (data.hasContent.quiz) {
        setActiveTab("quiz");
      }

      if (data.hasContent.quiz) {
        const quizRef = doc(
          db,
          "users",
          user.uid,
          "learningHistory",
          id,
          "quiz",
          "content"
        );
        const quizSnap = await getDoc(quizRef);

        if (quizSnap.exists()) {
          const quizContent = quizSnap.data();
          setQuizData({ questions: quizContent.questions });

          if (quizContent.userAnswers && quizContent.submittedAt) {
            setSelectedAnswers(quizContent.userAnswers);
            setQuizSubmitted(true);
            setShowResults(true);
          } else {
            setQuizSubmitted(false);
            setShowResults(false);
            setCurrentQuestionIndex(0);
          }
        }
      } else {
        setQuizData(null);
      }

      if (data.hasContent.flashcards) {
        const flashcardsRef = doc(
          db,
          "users",
          user.uid,
          "learningHistory",
          id,
          "flashcards",
          "content"
        );
        const flashcardsSnap = await getDoc(flashcardsRef);

        if (flashcardsSnap.exists()) {
          const flashcardsContent = flashcardsSnap.data();
          setFlashcardsData({ cards: flashcardsContent.cards });
          setCurrentCardIndex(0);
          setIsCardFlipped(false);
        }
      } else {
        setFlashcardsData(null);
      }

      setIsLoading(false);
    } catch (error) {
      setError("Failed to load learning data");
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    if (tab === "quiz" && !learningData?.hasContent.quiz && summaryData) {
      setActiveTab(tab);
      return;
    }
    if (tab === "flashcards" && !learningData?.hasContent.flashcards && summaryData) {
      setActiveTab(tab);
      return;
    }
    if (
      (tab === "summary" && learningData?.hasContent.summary) ||
      (tab === "quiz" && learningData?.hasContent.quiz) ||
      (tab === "flashcards" && learningData?.hasContent.flashcards)
    ) {
      setActiveTab(tab);
    }
  };

  const handleExportPDF = () => {
    const previousTitle = document.title;
    // Use the topic as the suggested PDF filename in the print dialog.
    if (summaryData?.title) {
      document.title = `${summaryData.title} - Summary`;
    }
    window.print();
    document.title = previousTitle;
  };

  const handleAnswerSelect = (optionIndex: number) => {
    setSelectedAnswers({ ...selectedAnswers, [currentQuestionIndex]: optionIndex });
  };

  const handleNextQuestion = () => {
    if (!quizData) return;
    if (currentQuestionIndex < quizData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowResults(true);
      setQuizSubmitted(true);
      storeUserAnswers();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleRestartQuiz = async () => {
    if (!quizData || !user || !id) return;
    try {
      setSelectedAnswers({});
      setShowResults(false);
      setQuizSubmitted(false);
      setCurrentQuestionIndex(0);

      const quizRef = doc(
        db,
        "users",
        user.uid,
        "learningHistory",
        id,
        "quiz",
        "content"
      );
      await updateDoc(quizRef, {
        userAnswers: null,
        submittedAt: null,
        score: null,
        totalQuestions: null,
      });
    } catch (error) {
      setToast({ message: "Failed to reset quiz", type: "error" });
      setTimeout(() => setToast(null), 5000);
    }
  };

  const calculateScore = () => {
    if (!quizData) return 0;
    return Object.keys(selectedAnswers).reduce((total, questionIdx) => {
      const qIdx = parseInt(questionIdx);
      return selectedAnswers[qIdx] === quizData.questions[qIdx].correctAnswer
        ? total + 1
        : total;
    }, 0);
  };

  const storeUserAnswers = async () => {
    try {
      if (!user || !id || !quizData) return;
      const score = Object.keys(selectedAnswers).reduce((total, questionIdx) => {
        const qIdx = parseInt(questionIdx);
        return selectedAnswers[qIdx] === quizData.questions[qIdx].correctAnswer
          ? total + 1
          : total;
      }, 0);

      const quizRef = doc(
        db,
        "users",
        user.uid,
        "learningHistory",
        id,
        "quiz",
        "content"
      );
      await updateDoc(quizRef, {
        userAnswers: selectedAnswers,
        submittedAt: new Date(),
        score: score,
        totalQuestions: quizData.questions.length,
      });
    } catch (error) {
      // silent
    }
  };

  const generateQuiz = async () => {
    if (!summaryData || !user || !id) {
      setToast({ message: "Summary data is required to generate a quiz", type: "error" });
      setTimeout(() => setToast(null), 5000);
      return;
    }

    setIsGeneratingQuiz(true);
    setError(null);

    try {
      const response = await fetch(
        "https://us-central1-einstein-90036.cloudfunctions.net/createQuizHttp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            topic: {
              title: summaryData.title,
              emoji: summaryData.emoji,
              summary: summaryData.summary,
            },
            userId: user.uid,
            learningId: id,
            difficulty: quizDifficulty,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to generate quiz");

      await fetchLearningData();
      setToast({
        message: `${quizDifficulty.charAt(0).toUpperCase() + quizDifficulty.slice(1)} quiz generated!`,
        type: "success",
      });
      setTimeout(() => setToast(null), 5000);
      setActiveTab("quiz");
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        type: "error",
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIsGeneratingQuiz(false);
    }
  };

  const generateFlashcards = async () => {
    if (!summaryData || !user || !id) {
      setToast({
        message: "Summary data is required to generate flashcards",
        type: "error",
      });
      setTimeout(() => setToast(null), 5000);
      return;
    }

    setIsGeneratingFlashcards(true);
    setError(null);

    try {
      const response = await fetch(
        "https://us-central1-einstein-90036.cloudfunctions.net/createFlashcardsHttp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            topic: {
              title: summaryData.title,
              emoji: summaryData.emoji,
              summary: summaryData.summary,
            },
            userId: user.uid,
            learningId: id,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to generate flashcards");

      await fetchLearningData();
      setToast({ message: "Flashcards generated!", type: "success" });
      setTimeout(() => setToast(null), 5000);
      setActiveTab("flashcards");
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        type: "error",
      });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIsGeneratingFlashcards(false);
    }
  };

  const handleNextCard = () => {
    if (!flashcardsData) return;
    if (currentCardIndex < flashcardsData.cards.length - 1) {
      setIsCardFlipped(false);
      setCurrentCardIndex((i) => i + 1);
    }
  };

  const handlePreviousCard = () => {
    if (currentCardIndex > 0) {
      setIsCardFlipped(false);
      setCurrentCardIndex((i) => i - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-2 border-t-[#47b3fe] border-slate-200 dark:border-gray-700 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
          <i className="fas fa-circle-exclamation text-red-500"></i>
        </div>
        <p className="text-slate-600 dark:text-slate-400">{error}</p>
        <button
          onClick={() => navigate("/home")}
          className="px-5 py-2.5 bg-[#47b3fe] hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 py-8 mx-auto max-w-4xl">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-3 ${
            toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
          }`}
        >
          <i className={`fas ${toast.type === "error" ? "fa-circle-xmark" : "fa-circle-check"}`}></i>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative overflow-hidden rounded-3xl mb-6 border border-slate-200/70 dark:border-gray-800 bg-gradient-to-br from-[#47b3fe]/10 via-indigo-500/[0.06] to-transparent dark:from-[#47b3fe]/10 dark:via-indigo-500/10 dark:to-transparent"
      >
        {/* Ambient glows */}
        <div className="pointer-events-none absolute -top-16 -right-12 w-56 h-56 bg-[#47b3fe]/20 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-12 w-56 h-56 bg-indigo-500/10 rounded-full blur-3xl" />

        <div className="relative flex items-start gap-5 p-6 md:p-8">
          <motion.div
            className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-2xl bg-white/80 dark:bg-gray-900/70 backdrop-blur-sm border border-white/60 dark:border-white/10 shadow-[0_10px_30px_-10px_rgba(71,179,254,0.55)] flex items-center justify-center text-3xl md:text-4xl flex-shrink-0"
            initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 16 }}
          >
            {learningData?.emoji}
          </motion.div>

          <div className="min-w-0 flex-1">
            <motion.h1
              className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {learningData?.title}
            </motion.h1>

            <motion.div
              className="mt-3 flex flex-wrap items-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              {learningData?.timestamp && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/70 dark:bg-gray-900/60 border border-slate-200/80 dark:border-gray-800 text-slate-600 dark:text-slate-300 backdrop-blur-sm">
                  <i className="far fa-calendar text-[#47b3fe]"></i>
                  {learningData.timestamp.toDate().toLocaleDateString()}
                </span>
              )}
              {learningData?.hasContent.summary && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/70 dark:bg-gray-900/60 border border-slate-200/80 dark:border-gray-800 text-slate-600 dark:text-slate-300 backdrop-blur-sm">
                  <i className="fas fa-book-open text-[#47b3fe]"></i>
                  Summary
                </span>
              )}
              {learningData?.hasContent.quiz && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/70 dark:bg-gray-900/60 border border-slate-200/80 dark:border-gray-800 text-slate-600 dark:text-slate-300 backdrop-blur-sm">
                  <i className="fas fa-circle-question text-[#47b3fe]"></i>
                  Quiz
                  <span className="capitalize text-slate-400 dark:text-slate-500">
                    · {learningData?.quizDifficulty || "medium"}
                  </span>
                </span>
              )}
              {learningData?.hasContent.flashcards && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/70 dark:bg-gray-900/60 border border-slate-200/80 dark:border-gray-800 text-slate-600 dark:text-slate-300 backdrop-blur-sm">
                  <i className="fas fa-clone text-[#47b3fe]"></i>
                  Flashcards
                </span>
              )}
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <Tab.Group
        selectedIndex={TABS.indexOf(activeTab)}
        onChange={(index) => handleTabChange(TABS[index])}
      >
        <Tab.List className="flex p-1.5 gap-1.5 bg-slate-100/80 dark:bg-gray-900/80 border border-slate-200/70 dark:border-gray-800 rounded-2xl mb-6">
          {[
            { label: "Summary", icon: "fa-book-open", tab: "summary" as TabType, disabled: !learningData?.hasContent.summary },
            { label: "Quiz", icon: "fa-circle-question", tab: "quiz" as TabType, disabled: !learningData?.hasContent.quiz && !summaryData, badge: !learningData?.hasContent.quiz && summaryData ? "+" : null },
            { label: "Flashcards", icon: "fa-clone", tab: "flashcards" as TabType, disabled: !learningData?.hasContent.flashcards && !summaryData, badge: !learningData?.hasContent.flashcards && summaryData ? "+" : null },
          ].map(({ label, icon, tab, disabled, badge }) => (
            <Tab
              key={tab}
              disabled={disabled}
              className={({ selected }) =>
                `w-full py-2.5 text-sm font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 focus:outline-none relative
                ${selected
                  ? "bg-white dark:bg-gray-800 text-slate-900 dark:text-white shadow-card"
                  : disabled
                  ? "text-slate-300 dark:text-slate-700 cursor-not-allowed"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                }`
              }
            >
              <i className={`fas ${icon} text-xs`}></i>
              {label}
              {badge && (
                <span className="ml-0.5 text-xs text-[#47b3fe] font-bold">+</span>
              )}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          {/* Summary Panel */}
          <Tab.Panel>
            {summaryData && (
              <div>
                {/* Toolbar (hidden in print) */}
                <div className="flex justify-end mb-4 print:hidden">
                  <button
                    onClick={handleExportPDF}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-xl shadow-sm hover:bg-slate-50 dark:hover:bg-gray-800 hover:text-slate-900 dark:hover:text-white transition-colors"
                  >
                    <i className="fas fa-file-arrow-down text-[#47b3fe]"></i>
                    Export PDF
                  </button>
                </div>

                {/* Paper document */}
                <div className="flex justify-center">
                  <motion.article
                    id="summary-document"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full max-w-[820px] bg-white rounded-2xl shadow-card border border-slate-200/70 px-8 py-12 md:px-16 md:py-16 font-serif"
                  >
                    {/* Title page header */}
                    <header className="text-center mb-12 pb-8 border-b border-slate-200">
                      <div className="text-5xl mb-4 leading-none">
                        {summaryData.emoji}
                      </div>
                      <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                        {summaryData.title}
                      </h1>
                      <p className="mt-4 text-[11px] font-sans font-semibold text-slate-400 uppercase tracking-[0.25em]">
                        Study Summary
                        {learningData?.timestamp && (
                          <>
                            {"  ·  "}
                            {learningData.timestamp.toDate().toLocaleDateString()}
                          </>
                        )}
                      </p>
                    </header>

                    {/* Body */}
                    {summaryData.sections && summaryData.sections.length > 0 ? (
                      <div className="space-y-10">
                        {summaryData.sections.map((section, i) => (
                          <section key={i} className="break-inside-avoid">
                            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 leading-snug">
                              <span className="text-[#47b3fe] mr-2">
                                {i + 1}.
                              </span>
                              {section.title}
                            </h2>
                            <div className="text-[15px] md:text-base">
                              <ReactMarkdown
                                components={documentMarkdownComponents}
                              >
                                {section.content}
                              </ReactMarkdown>
                            </div>
                          </section>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[15px] md:text-base">
                        <ReactMarkdown components={documentMarkdownComponents}>
                          {summaryData.summary}
                        </ReactMarkdown>
                      </div>
                    )}
                  </motion.article>
                </div>
              </div>
            )}
          </Tab.Panel>

          {/* Quiz Panel */}
          <Tab.Panel>
            {isGeneratingQuiz ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-10 h-10 border-2 border-t-[#47b3fe] border-slate-200 dark:border-gray-700 rounded-full animate-spin"></div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Generating {quizDifficulty} quiz questions…
                </p>
              </div>
            ) : !learningData?.hasContent.quiz && summaryData ? (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-8 text-center">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-circle-question text-[#47b3fe] text-lg"></i>
                </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Generate a Quiz
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 max-w-sm mx-auto">
                  Test your knowledge with a custom quiz based on this summary.
                </p>

                <div className="mb-6">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    Difficulty
                  </p>
                  <div className="flex justify-center gap-2">
                    {(["easy", "medium", "hard"] as QuizDifficulty[]).map((d) => (
                      <button
                        key={d}
                        onClick={() => setQuizDifficulty(d)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150 capitalize
                          ${
                            quizDifficulty === d
                              ? "bg-[#47b3fe] text-white shadow-sm"
                              : "bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-gray-700"
                          }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={generateQuiz}
                  disabled={isGeneratingQuiz}
                  className="px-6 py-2.5 bg-[#47b3fe] hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Generate Quiz
                </button>
              </div>
            ) : (
              <>
                {quizData && quizData.questions.length > 0 && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key="quiz"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.2 }}
                    >
                      {!showResults ? (
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-6">
                          {/* Progress */}
                          <div className="mb-6">
                            <div className="flex justify-between text-xs text-slate-400 mb-2">
                              <span>Question {currentQuestionIndex + 1} of {quizData.questions.length}</span>
                              <span>{Math.round(((currentQuestionIndex + 1) / quizData.questions.length) * 100)}%</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#47b3fe] rounded-full transition-all duration-300"
                                style={{ width: `${((currentQuestionIndex + 1) / quizData.questions.length) * 100}%` }}
                              />
                            </div>
                          </div>

                          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-5 leading-snug">
                            {quizData.questions[currentQuestionIndex].question}
                          </h2>

                          <div className="space-y-2.5">
                            {quizData.questions[currentQuestionIndex].options.map(
                              (option, optionIndex) => {
                                const isSelected =
                                  selectedAnswers[currentQuestionIndex] === optionIndex;
                                return (
                                  <button
                                    key={optionIndex}
                                    onClick={() => handleAnswerSelect(optionIndex)}
                                    disabled={showResults}
                                    className={`w-full text-left p-4 rounded-xl transition-all duration-200 border text-sm flex items-center gap-3
                                      ${
                                        isSelected
                                          ? "border-[#47b3fe] bg-blue-50 dark:bg-blue-950/30 text-slate-900 dark:text-white"
                                          : "border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800 text-slate-700 dark:text-slate-300"
                                      }
                                      ${selectedAnswers[currentQuestionIndex] !== undefined && !isSelected ? "opacity-40" : ""}`}
                                  >
                                    <div
                                      className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold
                                        ${isSelected
                                          ? "bg-[#47b3fe] text-white"
                                          : "bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-slate-400"
                                        }`}
                                    >
                                      {String.fromCharCode(65 + optionIndex)}
                                    </div>
                                    {option}
                                  </button>
                                );
                              }
                            )}
                          </div>

                          <div className="flex justify-between items-center mt-6">
                            <button
                              onClick={handlePreviousQuestion}
                              className={`px-4 py-2 text-sm rounded-xl font-medium transition-colors ${
                                currentQuestionIndex > 0
                                  ? "bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-gray-700"
                                  : "bg-slate-50 dark:bg-gray-950 text-slate-300 dark:text-slate-700 cursor-not-allowed"
                              }`}
                              disabled={currentQuestionIndex === 0}
                            >
                              Previous
                            </button>
                            <span className="text-xs text-slate-400">
                              {currentQuestionIndex + 1} / {quizData.questions.length}
                            </span>
                            <button
                              onClick={handleNextQuestion}
                              className={`px-4 py-2 text-sm rounded-xl font-medium transition-colors ${
                                selectedAnswers[currentQuestionIndex] !== undefined
                                  ? "bg-[#47b3fe] hover:bg-blue-500 text-white"
                                  : "bg-slate-100 dark:bg-gray-800 text-slate-300 dark:text-slate-700 cursor-not-allowed"
                              }`}
                              disabled={selectedAnswers[currentQuestionIndex] === undefined}
                            >
                              {currentQuestionIndex === quizData.questions.length - 1
                                ? "Submit Quiz"
                                : "Next"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-8 max-w-3xl mx-auto">
                          <h2 className="text-2xl font-bold mb-2 text-center text-slate-900 dark:text-white">
                            Quiz Complete!
                          </h2>
                          <div className="mb-8 text-center">
                            <div className="text-5xl font-bold text-[#47b3fe] mb-1">
                              {calculateScore()}/{quizData.questions.length}
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm">
                              {calculateScore()} out of {quizData.questions.length} correct
                            </p>
                          </div>

                          <div className="mb-8">
                            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                              Question Review
                            </h3>
                            <div className="space-y-4">
                              {quizData.questions.map((question, qIndex) => {
                                const userAnswer = selectedAnswers[qIndex];
                                return (
                                  <div
                                    key={qIndex}
                                    className="bg-slate-50 dark:bg-gray-950 rounded-xl p-4 border border-slate-100 dark:border-gray-800"
                                  >
                                    <p className="font-medium text-slate-800 dark:text-slate-200 text-sm mb-3">
                                      {qIndex + 1}. {question.question}
                                    </p>
                                    <div className="space-y-1.5 pl-2">
                                      {question.options.map((option, oIndex) => {
                                        const isUserAnswer = userAnswer === oIndex;
                                        const isCorrectAnswer = question.correctAnswer === oIndex;
                                        return (
                                          <div
                                            key={oIndex}
                                            className={`p-3 rounded-xl flex items-center gap-3 text-sm
                                              ${isCorrectAnswer
                                                ? "bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800"
                                                : isUserAnswer
                                                ? "bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800"
                                                : "bg-white dark:bg-gray-900 border border-slate-100 dark:border-gray-800"
                                              }`}
                                          >
                                            <div
                                              className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold
                                                ${isCorrectAnswer
                                                  ? "bg-green-500 text-white"
                                                  : isUserAnswer && !isCorrectAnswer
                                                  ? "bg-red-500 text-white"
                                                  : "bg-slate-200 dark:bg-gray-700 text-slate-500 dark:text-slate-400"
                                                }`}
                                            >
                                              {["A","B","C","D","E"][oIndex]}
                                            </div>
                                            <span
                                              className={
                                                isCorrectAnswer
                                                  ? "text-green-700 dark:text-green-400"
                                                  : isUserAnswer
                                                  ? "text-red-700 dark:text-red-400"
                                                  : "text-slate-600 dark:text-slate-400"
                                              }
                                            >
                                              {option}
                                            </span>
                                            <div className="ml-auto text-xs font-medium">
                                              {isCorrectAnswer && (
                                                <span className="text-green-600 dark:text-green-400">✓ Correct</span>
                                              )}
                                              {isUserAnswer && !isCorrectAnswer && (
                                                <span className="text-red-600 dark:text-red-400">✗ Your answer</span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          <div className="text-center">
                            <button
                              onClick={handleRestartQuiz}
                              className="px-6 py-2.5 bg-[#47b3fe] hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition-colors"
                            >
                              Try Again
                            </button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                )}
              </>
            )}
          </Tab.Panel>

          {/* Flashcards Panel */}
          <Tab.Panel>
            {isGeneratingFlashcards ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-10 h-10 border-2 border-t-[#47b3fe] border-slate-200 dark:border-gray-700 rounded-full animate-spin"></div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Generating flashcards…
                </p>
              </div>
            ) : !learningData?.hasContent.flashcards && summaryData ? (
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800 p-8 text-center">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-clone text-[#47b3fe] text-lg"></i>
                </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Generate Flashcards
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 max-w-sm mx-auto">
                  Create flashcards from this summary to practice active recall.
                </p>
                <button
                  onClick={generateFlashcards}
                  disabled={isGeneratingFlashcards}
                  className="px-6 py-2.5 bg-[#47b3fe] hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Generate Flashcards
                </button>
              </div>
            ) : (
              flashcardsData &&
              flashcardsData.cards.length > 0 && (
                <div className="max-w-2xl mx-auto">
                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-slate-400 mb-2">
                      <span>
                        Card {currentCardIndex + 1} of{" "}
                        {flashcardsData.cards.length}
                      </span>
                      <span>
                        {Math.round(
                          ((currentCardIndex + 1) /
                            flashcardsData.cards.length) *
                            100
                        )}
                        %
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#47b3fe] rounded-full transition-all duration-300"
                        style={{
                          width: `${
                            ((currentCardIndex + 1) /
                              flashcardsData.cards.length) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Card — click to flip */}
                  <button
                    onClick={() => setIsCardFlipped((f) => !f)}
                    className="w-full"
                    style={{ perspective: "1200px" }}
                    aria-label="Flip flashcard"
                  >
                    <div
                      className="relative w-full h-72 transition-transform duration-500"
                      style={{
                        transformStyle: "preserve-3d",
                        transform: isCardFlipped
                          ? "rotateY(180deg)"
                          : "rotateY(0deg)",
                      }}
                    >
                      {/* Front */}
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-slate-100 dark:border-gray-800"
                        style={{ backfaceVisibility: "hidden" }}
                      >
                        <span className="text-[11px] font-semibold text-[#47b3fe] uppercase tracking-[0.2em] mb-4">
                          Question
                        </span>
                        <p className="text-xl font-semibold text-slate-900 dark:text-white leading-snug">
                          {flashcardsData.cards[currentCardIndex].front}
                        </p>
                        <span className="absolute bottom-4 text-xs text-slate-400 dark:text-slate-600">
                          <i className="fas fa-rotate mr-1.5"></i>Tap to reveal
                          answer
                        </span>
                      </div>

                      {/* Back */}
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-[#47b3fe] to-indigo-600 rounded-2xl shadow-card"
                        style={{
                          backfaceVisibility: "hidden",
                          transform: "rotateY(180deg)",
                        }}
                      >
                        <span className="text-[11px] font-semibold text-white/80 uppercase tracking-[0.2em] mb-4">
                          Answer
                        </span>
                        <p className="text-lg font-medium text-white leading-relaxed">
                          {flashcardsData.cards[currentCardIndex].back}
                        </p>
                        <span className="absolute bottom-4 text-xs text-white/70">
                          <i className="fas fa-rotate mr-1.5"></i>Tap to flip
                          back
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-6">
                    <button
                      onClick={handlePreviousCard}
                      disabled={currentCardIndex === 0}
                      className={`px-4 py-2 text-sm rounded-xl font-medium transition-colors ${
                        currentCardIndex > 0
                          ? "bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-gray-700"
                          : "bg-slate-50 dark:bg-gray-950 text-slate-300 dark:text-slate-700 cursor-not-allowed"
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-xs text-slate-400">
                      {currentCardIndex + 1} / {flashcardsData.cards.length}
                    </span>
                    <button
                      onClick={handleNextCard}
                      disabled={
                        currentCardIndex === flashcardsData.cards.length - 1
                      }
                      className={`px-4 py-2 text-sm rounded-xl font-medium transition-colors ${
                        currentCardIndex < flashcardsData.cards.length - 1
                          ? "bg-[#47b3fe] hover:bg-blue-500 text-white"
                          : "bg-slate-100 dark:bg-gray-800 text-slate-300 dark:text-slate-700 cursor-not-allowed"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )
            )}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default LearningDetail;
